import React, { useState, useEffect } from 'react';
import { itemAPI, masterAPI, skuAPI } from '../services/api';
import DataTable from '../components/ui/DataTable';
import SearchableSelect from '../components/ui/SearchableSelect';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Package, X, Save, Zap, Hash, AlertTriangle } from 'lucide-react';

const EMPTY = {
  itemName: '', categoryID: '', purityID: '', designID: '',
  hsnCode: '7106', gstPercent: 3, unit: 'GMS', standardWeight: 0,
  makingCharges: 0, barcode: '', skuNo: '', shortCode: '', size: '', gender: 'M', isManualSKU: false,
  showSKUSize: false
};

export default function ItemsPage() {
  const [data, setData]             = useState([]);
  const [categories, setCategories] = useState([]);
  const [purities, setPurities]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(false);
  const [form, setForm]             = useState(EMPTY);
  const [editID, setEditID]         = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [skuConfig, setSkuConfig]   = useState({ companyPrefix: 'CQ', separator: '', charCount: 2, seqPadding: 5, currentSeq: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const [items, cats, purs, skuRes] = await Promise.all([
        itemAPI.getAll(),
        masterAPI.getCategories(),
        masterAPI.getPurities(),
        skuAPI.get().catch(() => null)
      ]);
      setData(items.data.data || []);
      setCategories(cats.data.data || []);
      setPurities(purs.data.data || []);
      if (skuRes?.data?.data) {
        const d = skuRes.data.data;
        setSkuConfig({
          companyPrefix: d.CompanyPrefix || d.companyprefix || 'CQ',
          separator:     d.Separator     || d.separator     || '',
          charCount:     d.CharCount     || d.charcount     || 2,
          seqPadding:    d.SeqPadding    || d.seqpadding    || 5,
          currentSeq:    d.CurrentSeq    || d.currentseq    || 0,
        });
      }
    } catch { toast.error('Failed to load items'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const buildAutoSKU = (itemName, categoryID, shortCode = '') => {
    const prefix = skuConfig.companyPrefix || 'CQ';
    const charCount = 2;
    const sep = skuConfig.separator || '';

    // Use item shortCode if set, otherwise derive from category/item name
    let itemCode = '';
    if (shortCode && String(shortCode).trim()) {
      itemCode = String(shortCode).replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, charCount);
    } else {
      const catObj = categories.find(c => String(c.categoryID) === String(categoryID));
      const nameToUse = catObj?.categoryName || itemName || '';
      itemCode = nameToUse ? nameToUse.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, charCount) : '';
    }

    const parts = [];
    if (prefix) parts.push(prefix);
    if (itemCode) parts.push(itemCode);

    return parts.join(sep);
  };

  // A real custom SKU if one was saved; the DB's internal fallback code
  // (ITM000001 style) is not a SKU, so those show the auto-built one
  // (prefix + short code, e.g. CQ + BR = CQBR) instead.
  const customSKU = (row) => {
    const stored = row.skuNo || row.itemCode || '';
    return stored && !/^ITM\d+$/i.test(stored) ? stored : null;
  };
  const displaySKU = (row) =>
    customSKU(row) || buildAutoSKU(row.itemName, row.categoryID, row.shortCode || '');

  const openAdd  = () => { setForm(EMPTY); setEditID(null); setModal(true); };
  const openEdit = (row) => {
    const hasCustomSku = !!customSKU(row);
    setForm({
      itemName: row.itemName, categoryID: row.categoryID, purityID: row.purityID,
      designID: row.designID || '', hsnCode: row.hsnCode || '7106',
      gstPercent: row.gstPercent, unit: row.unit, standardWeight: row.standardWeight,
      makingCharges: row.makingCharges, barcode: row.barcode || '',
      skuNo: displaySKU(row),
      shortCode: row.shortCode || '',
      showSKUSize: !!row.showSKUSize,
      isManualSKU: hasCustomSku
    });
    setEditID(row.itemID);
    setModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await itemAPI.delete(deleteTarget.itemID);
      toast.success(`Item "${deleteTarget.itemName}" deleted`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const updateFormField = (key, val) => {
    setForm(prev => {
      const next = { ...prev, [key]: val };
      if (key === 'skuNo') {
        next.isManualSKU = true;
      } else if ((key === 'categoryID' || key === 'itemName' || key === 'shortCode') && !prev.isManualSKU) {
        next.skuNo = buildAutoSKU(
          key === 'itemName' ? val : prev.itemName,
          key === 'categoryID' ? val : prev.categoryID,
          key === 'shortCode' ? val : prev.shortCode
        );
      }
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.itemName || !form.categoryID || !form.purityID) return toast.error('Name, category and purity required');
    setSaving(true);
    try {
      const payload = {
        ...form,
        categoryID: +form.categoryID,
        purityID: +form.purityID,
        designID: form.designID ? +form.designID : null,
        itemCode: form.skuNo || undefined
      };
      if (editID) { await itemAPI.update(editID, { ...payload, isActive: true }); toast.success('Item updated'); }
      else { await itemAPI.create(payload); toast.success('Item created'); }
      setModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const inp = (k) => ({ value: form[k], onChange: e => updateFormField(k, e.target.value) });

  const columns = [
    { header: 'SKU No', key: 'skuNo', width: 120, render: r => <code style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)' }}>{displaySKU(r)}</code> },
    { header: 'Item Name', key: 'itemName', render: r => <span style={{ fontWeight: 600 }}>{r.itemName}</span> },
    { header: 'Short Code', key: 'shortCode', width: 100, render: r => r.shortCode ? <code style={{ fontSize: 11, color: '#7c3aed' }}>{r.shortCode}</code> : <span className="text-muted">—</span> },
    { header: 'Category', key: 'categoryName' },
    { header: 'Purity', key: 'purityCode', width: 80, render: r => <span className="badge badge-navy">{r.purityCode}</span> },
    { header: 'Unit', key: 'unit', width: 70 },
    { header: 'Std. Weight', key: 'standardWeight', align: 'right', render: r => r.standardWeight ? `${r.standardWeight}g` : '—' },
    { header: 'GST%', key: 'gstPercent', align: 'right', width: 70, render: r => `${r.gstPercent}%` },
    { header: 'Actions', align: 'center', width: 100, render: r => (
      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(r)} title="Edit Item"><Edit2 size={14} /></button>
        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setDeleteTarget(r)} title="Delete Item" style={{ color: '#ef4444' }}><Trash2 size={14} /></button>
      </div>
    )},
  ];

  return (
    <div>
      <div className="flex-between mb-6">
        <div><h1>Items</h1><p className="text-muted">{data.length} items in master</p></div>
        <button className="btn btn-amber" onClick={openAdd}><Plus size={16} /> Add Item</button>
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ padding: '16px 20px' }}>
            <DataTable data={data} columns={columns} loading={loading}
              searchKeys={['itemName','skuNo','itemCode','categoryName','purityCode','barcode']}
              emptyIcon={<Package size={36} />} emptyText="No items — add your first item" />
          </div>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3>{editID ? 'Edit Item' : 'Add Item'}</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="grid-2" style={{ gap: 14 }}>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Item Name *</label>
                    <input className="form-control" placeholder="e.g. Silver Coin 10g" required {...inp('itemName')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category *</label>
                    <SearchableSelect
                      value={String(form.categoryID)}
                      onChange={val => updateFormField('categoryID', val)}
                      options={categories.map(c => ({ value: String(c.categoryID), label: c.categoryName }))}
                      placeholder="Select Category"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Purity *</label>
                    <SearchableSelect
                      value={String(form.purityID)}
                      onChange={val => updateFormField('purityID', val)}
                      options={purities.map(p => ({ value: String(p.purityID), label: `${p.purityCode} — ${p.purityName}` }))}
                      placeholder="Select Purity"
                    />
                  </div>

                  {/* SKU Field (Auto generated CQBR... / Manual) */}
                  <div className="form-group" style={{ gridColumn: '1 / -1', background: 'rgba(59,130,246,0.04)', padding: 12, borderRadius: 10, border: '1px solid rgba(59,130,246,0.15)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Zap size={14} color="var(--primary)" />
                        <strong style={{ fontSize: 12, color: 'var(--primary)' }}>SKU No (Auto / Manual)</strong>
                      </div>
                      {form.skuNo && (
                        <span style={{ fontSize: 11, fontWeight: 800, fontFamily: 'monospace', color: '#f59e0b', background: '#fffbe6', padding: '2px 8px', borderRadius: 6, border: '1px solid #ffe58f' }}>
                          Preview: {form.skuNo}
                        </span>
                      )}
                    </div>
                    <input className="form-control" placeholder="e.g. CQSS or CQBR" style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)' }} {...inp('skuNo')} />
                    <small style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4, display: 'block' }}>
                      Auto-generated Item Master Code (e.g. <strong>CQ</strong> + <strong>BR</strong> = <strong>CQBR</strong>). Edit manually if needed.
                    </small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Unit</label>
                    <SearchableSelect
                      value={form.unit}
                      onChange={val => updateFormField('unit', val)}
                      options={['GMS', 'PCS', 'KG'].map(u => ({ value: u, label: u }))}
                      placeholder="Select Unit"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">HSN Code</label>
                    <input className="form-control" placeholder="7106" {...inp('hsnCode')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">GST %</label>
                    <input className="form-control" type="number" step="0.5" {...inp('gstPercent')} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Item Short Code</label>
                    <input className="form-control" placeholder="e.g. SC, RC, BR (short identifier)" {...inp('shortCode')} />
                    <small style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4, display: 'block' }}>
                      Optional short code for quick reference (e.g. <strong>SC</strong> for Silver Coin).
                    </small>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                      <input type="checkbox" checked={form.showSKUSize}
                        onChange={e => updateFormField('showSKUSize', e.target.checked)} />
                      Show SKU Size
                    </label>
                    <small style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4, display: 'block' }}>
                      When enabled, the item's size is shown along with the SKU.
                    </small>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <Save size={15} />}
                  {editID ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header" style={{ borderBottom: '1px solid #fee2e2' }}>
              <h3 style={{ color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={20} color="#dc2626" /> Delete Item
              </h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setDeleteTarget(null)}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <p style={{ margin: 0, fontSize: 14 }}>
                Are you sure you want to delete item <strong>"{deleteTarget.itemName}"</strong> ({deleteTarget.skuNo || deleteTarget.itemCode})?
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button type="button" className="btn" style={{ background: '#dc2626', color: '#fff' }} onClick={handleDelete}>
                Delete Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
